import type { ExpressionSpecification } from 'mapbox-gl';
import { type ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';
import { Source } from 'react-map-gl/mapbox';
import type MapLocation from '@alltrails/amplitude/enums/MapLocation';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import useMap from '../../hooks/useMap';
import { useAppSelector } from '../../redux';
import { trailheadsMinZoom } from '../../utils/countTrailheadsInView';
import { trailheadsOverlayCollapsedActiveId, trailheadsOverlayCollapsedId, trailheadsOverlayLayerId } from '../../utils/layers';
import { buildTrailheadResultExpressions, parseTrailheadTrailIds } from '../../utils/trailheadTrailResults';
import TrailheadVectorPinLayer from '../TrailheadVectorPinLayer/TrailheadVectorPinLayer';
import ActiveTrailPopup from '../TrailResults/ActiveTrailPopup';

type TrailheadVectorOverlayProps = {
  mapLocation?: MapLocation | string; // Accept string for legacy PageStrings support
} & Pick<ComponentProps<typeof ActiveTrailPopup>, 'detailedCardLocation'>;

const apiKey = process.env.NEXT_PUBLIC_API_KEY!;
const sourceId = 'trailheads-overlay';
const sourceLayer = 'trailheads';
const inactiveExpression: ExpressionSpecification = ['literal', false];
const neverHoveredExpression: ExpressionSpecification = ['==', ['id'], -1];
const hasTrailsFilter: ExpressionSpecification = ['>', ['coalesce', ['get', 'trail_count'], 0], 0];

const sameTrailIds = (current: Record<string, number[]>, next: Record<string, number[]>) => {
  const currentIds = Object.keys(current);
  return (
    currentIds.length === Object.keys(next).length &&
    currentIds.every(trailheadId => {
      const nextTrailIds = next[trailheadId];
      return (
        nextTrailIds !== undefined &&
        nextTrailIds.length === current[trailheadId].length &&
        nextTrailIds.every((trailId, index) => trailId === current[trailheadId][index])
      );
    })
  );
};

export default function TrailheadVectorOverlay({ detailedCardLocation, mapLocation }: TrailheadVectorOverlayProps) {
  const publicBaseUrl = usePublicBaseUrl();
  const map = useMap();
  const trailResults = useAppSelector(state => state.map.trailResults);
  const [trailIdsByTrailheadId, setTrailIdsByTrailheadId] = useState<Record<string, number[]>>({});
  const tiles = [`${publicBaseUrl}/wmts/overlay/trailheads/{z}/{x}/{y}?key=${apiKey}`];

  const analyticsInfo = useMemo(
    () => (detailedCardLocation && mapLocation ? { detailedCardLocation, page: mapLocation } : undefined),
    [detailedCardLocation, mapLocation]
  );
  const { isFavorite: isSavedUtil, isComplete: isCompletedUtil } = useReduxListItems(undefined, undefined, analyticsInfo);
  const isTrailSaved = useCallback((trailId: number) => isSavedUtil('trail', trailId), [isSavedUtil]);
  const isTrailCompleted = useCallback((trailId: number) => isCompletedUtil('trail', trailId), [isCompletedUtil]);

  const { isClosedExpression, isCompletedExpression, isSavedExpression, matchedTrailheadsFilter, popularityExpression, trailCountExpression } =
    useMemo(
      () => buildTrailheadResultExpressions(trailResults, trailIdsByTrailheadId, { isTrailCompleted, isTrailSaved }),
      [isTrailCompleted, isTrailSaved, trailIdsByTrailheadId, trailResults]
    );
  const pinFilter = useMemo<ExpressionSpecification>(() => ['all', hasTrailsFilter, matchedTrailheadsFilter], [matchedTrailheadsFilter]);

  useEffect(() => {
    if (!map) {
      return undefined;
    }

    const updateTrailIdsByTrailheadId = () => {
      const nextTrailIds: Record<string, number[]> = {};
      map.querySourceFeatures(sourceId, { sourceLayer }).forEach(feature => {
        if (feature.id !== undefined) {
          nextTrailIds[String(feature.id)] = parseTrailheadTrailIds(feature.properties?.trail_ids);
        }
      });
      setTrailIdsByTrailheadId(currentTrailIds => (sameTrailIds(currentTrailIds, nextTrailIds) ? currentTrailIds : nextTrailIds));
    };

    updateTrailIdsByTrailheadId();
    map.on('idle', updateTrailIdsByTrailheadId);
    return () => {
      map.off('idle', updateTrailIdsByTrailheadId);
    };
  }, [map]);

  return (
    <Source id={sourceId} minzoom={trailheadsMinZoom} tiles={tiles} type="vector">
      <TrailheadVectorPinLayer
        collapsedActiveLayerId={trailheadsOverlayCollapsedActiveId}
        collapsedLayerId={trailheadsOverlayCollapsedId}
        filter={pinFilter}
        isClosedExpression={isClosedExpression}
        isCollapsedExpression={inactiveExpression}
        isCompletedExpression={isCompletedExpression}
        isHoveredExpression={neverHoveredExpression}
        isSavedExpression={isSavedExpression}
        isSelectedExpression={neverHoveredExpression}
        layerId={trailheadsOverlayLayerId}
        popularityExpression={popularityExpression}
        sourceId={sourceId}
        sourceLayer={sourceLayer}
        trailCountExpression={trailCountExpression}
      />
    </Source>
  );
}
